import { useState } from "react";
import { RUBRIC_LEVEL_DESCRIPTIONS } from "./rubricLevelDescriptions";

const API_URL = "https://hs-topics-api.dumptruckmedia.com";
// Shared client key the backend Worker checks (worker.js). This ships in the
// public bundle -- it isn't a real secret, it just keeps casual/opportunistic
// abuse off the endpoint alongside the Worker's origin allowlist. See
// worker.js for the full threat-model note.
const APP_KEY = "2c2d4d1796fc84e4c99bf3d12aab864e729006a911364e3f";
// Signed school_id:role token minted by dtm-api (GET /api/schools/:id/topicgen-token)
// and appended by the Dashboard link as ?t=... (2026-08-25). Replaces the old plain,
// client-editable ?role=student param -- the Worker now verifies this token itself
// on every request and is the real enforcement point, not this tab-hide. Reading the
// role back out of it here is purely a UX nicety (which tab to show); a missing or
// stale token doesn't unlock anything, it just means every request will be rejected
// by the Worker with a clear error until the page is reloaded with a fresh link.
const SANDBOX_TOKEN = new URLSearchParams(window.location.search).get("t") || "";
const IS_STUDENT = SANDBOX_TOKEN.split(":")[1] === "student";

const SUBJECTS = [
  "English Language Arts",
  "Mathematics",
  "Biology",
  "Chemistry",
  "Physics / Earth Science",
  "World History",
  "US History",
  "American Government",
  "Economics",
  "Health",
  "Physical Education",
  "Computer Science / Coding",
  "Journalism",
  "Creative Writing",
  "Theater",
  "Art",
  "Music",
  "Foreign Language",
  "Personal Finance",
  "Psychology",
  "Robotics / Engineering"
];

const GRADE_LEVELS = ["9th Grade", "10th Grade", "11th Grade", "12th Grade"];

const SUBJECT_STANDARDS = {
  "English Language Arts": ["Reading Literature", "Reading Informational Text", "Writing Arguments", "Speaking & Listening", "Language & Vocabulary", "Research & Inquiry"],
  "Mathematics": ["Algebra & Functions", "Geometry & Trigonometry", "Statistics & Probability", "Pre-Calculus & Calculus", "Mathematical Reasoning & Proof"],
  "Biology": ["Cell Biology", "Genetics & Evolution", "Ecology & Ecosystems", "Human Body Systems", "Scientific Inquiry"],
  "Chemistry": ["Atomic Structure", "Chemical Reactions", "Thermodynamics", "Acids & Bases", "Scientific Inquiry"],
  "Physics / Earth Science": ["Motion & Forces", "Energy & Waves", "Electricity & Magnetism", "Earth Systems", "Scientific Inquiry"],
  "World History": ["Ancient Civilizations", "Medieval & Renaissance", "Age of Exploration", "Revolutions & Industrialization", "20th Century Global Conflicts", "Historical Thinking Skills"],
  "US History": ["Founding & Constitution", "Civil War & Reconstruction", "Progressive Era", "World Wars", "Civil Rights Movement", "Modern America"],
  "American Government": ["Constitutional Principles", "Branches of Government", "Civil Rights & Liberties", "Electoral Process", "Public Policy"],
  "Economics": ["Microeconomics", "Macroeconomics", "Personal Finance", "Global Economics", "Supply & Demand"],
  "Health": ["Mental & Emotional Health", "Physical Wellness", "Nutrition & Fitness", "Substance Prevention", "Sexual Health Education"],
  "Physical Education": ["Movement & Fitness", "Team Sports", "Lifetime Activities", "Health-Related Fitness"],
  "Computer Science / Coding": ["Programming Fundamentals", "Data Structures", "Web Development", "Cybersecurity", "Artificial Intelligence"],
  "Journalism": ["News Writing", "Investigative Reporting", "Media Literacy", "Ethics in Journalism", "Broadcast & Digital Media"],
  "Creative Writing": ["Fiction Writing", "Poetry", "Screenwriting", "Personal Narrative", "Revision & Craft"],
  "Theater": ["Acting & Performance", "Playwriting", "Production & Design", "Theater History", "Directing"],
  "Art": ["Drawing & Painting", "Digital Art", "Art History", "Sculpture & 3D Design", "Portfolio Development"],
  "Music": ["Music Theory", "Performance", "Music History", "Composition", "Music Technology"],
  "Foreign Language": ["Speaking & Pronunciation", "Reading & Writing", "Cultural Connections", "Grammar & Vocabulary", "Conversational Fluency"],
  "Personal Finance": ["Budgeting & Saving", "Credit & Debt", "Investing & Retirement", "Taxes & Insurance", "Entrepreneurship"],
  "Psychology": ["Human Development", "Cognition & Memory", "Social Psychology", "Mental Health & Disorders", "Research Methods"],
  "Robotics / Engineering": ["Engineering Design Process", "Programming & Automation", "Mechanical Systems", "Electronics & Circuits", "Problem Solving & Innovation"]
};

const VERBAL_SKILLS = [
  { id: "clarity", label: "Clarity of Expression", desc: "Ideas are communicated in a way that listeners can easily follow" },
  { id: "organization", label: "Organization & Flow", desc: "Content is structured with a clear beginning, middle, and end" },
  { id: "vocabulary", label: "Vocabulary & Word Choice", desc: "Uses precise, subject-appropriate language effectively" },
  { id: "engagement", label: "Listener Engagement", desc: "Tone, pacing, and delivery hold the audience's attention" },
  { id: "evidence", label: "Use of Evidence", desc: "Claims are supported with facts, examples, or reasoning" },
  { id: "voice", label: "Authentic Voice", desc: "Speaker's personality and perspective come through naturally" },
  { id: "thesis", label: "Thesis & Argumentation", desc: "A clear position is established and effectively defended throughout" },
  { id: "audience", label: "Audience Awareness", desc: "Content is adapted appropriately for the intended audience" },
];

const RATINGS = [
  { value: 1, label: "Getting Started" },
  { value: 2, label: "Building Skills" },
  { value: 3, label: "Demonstrating Proficiency" },
  { value: 4, label: "Excelling" },
];

export default function PodcastAppHS() {
  const [activeTab, setActiveTab] = useState("generate");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  const [standard, setStandard] = useState("");
  const [customTopic, setCustomTopic] = useState("");
  const [podcastFormat, setPodcastFormat] = useState("");
  const [ideas, setIdeas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [studentName, setStudentName] = useState("");
  const [episodeTitle, setEpisodeTitle] = useState("");
  const [ratings, setRatings] = useState({});
  const [feedback, setFeedback] = useState({});
  const [assessLoading, setAssessLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [assessError, setAssessError] = useState("");

  const availableStandards = subject ? SUBJECT_STANDARDS[subject] || [] : [];

  const generateIdeas = async () => {
    if (!subject || !grade) { setError("Please select a subject and grade level."); return; }
    setError(""); setLoading(true); setIdeas(null);
    try {
      const prompt = `You are a creative high school curriculum specialist. Generate 5 engaging podcast episode ideas for students.\n\nSubject: ${subject}\nGrade Level: ${grade}\n${standard ? `Standards Focus: ${standard}` : ""}\n${customTopic ? `Teacher's Topic/Unit: ${customTopic}` : ""}\n${podcastFormat ? `Preferred Format: ${podcastFormat}` : ""}\n\nIMPORTANT: These podcast ideas are for high school STUDENTS (grades 9-12) to create themselves in the classroom. The episodes should reflect the maturity, critical thinking, and college-prep expectations of high school. Students should be researching real-world issues, forming arguments, and engaging with complex topics appropriate for their age. The tone should be intellectually engaging and reflect what a high school student would genuinely want to explore and discuss with their peers.\n\nINTERVIEW RULES — FOLLOW STRICTLY:\n- Never suggest interviewing celebrities, public figures, politicians, doctors, scientists, or any authority figures outside the school\n- The only acceptable interview subjects are: teachers at the school, or other students\n- Unless the teacher specifically selected "Interview style" as the preferred format, no more than 1 of the 5 ideas should involve an interview format\n- If interview style WAS selected, all interviews must still only involve teachers or other students\n\nFor each idea, provide:\n1. A compelling episode title\n2. A 2-sentence description of what students will research and discuss\n3. The key learning objective it addresses\n4. A suggested format (debate, investigative report, narrative, roundtable, documentary, etc.)\n5. One hook question to get students excited about making this episode\n\nReturn ONLY valid JSON in this exact format, no other text:\n{\n  "ideas": [\n    {\n      "title": "...",\n      "description": "...",\n      "objective": "...",\n      "format": "...",\n      "hook": "..."\n    }\n  ]\n}`;

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-SandBox-Key": APP_KEY, "X-SandBox-Token": SANDBOX_TOKEN },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, mode: "topic_generation", messages: [{ role: "user", content: prompt }] })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong generating ideas. Please try again.");
        setLoading(false);
        return;
      }
      const text = data.content?.find(b => b.type === "text")?.text || "";
      const clean = text.replace(/```json|```/g, "").trim();
      setIdeas(JSON.parse(clean));
    } catch (e) {
      setError("Something went wrong generating ideas. Please try again.");
    }
    setLoading(false);
  };

  const generateFeedback = async () => {
    if (Object.keys(ratings).length < VERBAL_SKILLS.length) { return; }
    setAssessLoading(true); setAiSuggestions(null); setAssessError("");
    try {
      const ratingsSummary = VERBAL_SKILLS.map(s => `${s.label}: ${RATINGS.find(r => r.value === ratings[s.id])?.label || "not rated"}`).join("\n");
      const feedbackSummary = VERBAL_SKILLS.map(s => feedback[s.id] ? `${s.label}: ${feedback[s.id]}` : "").filter(Boolean).join("\n");

      const prompt = `You are a supportive high school teacher writing podcast assessment feedback. Generate encouraging, growth-oriented feedback appropriate for a high school student.\n\nStudent: ${studentName || "Student"}\nEpisode: ${episodeTitle || "Podcast Episode"}\n\nRatings:\n${ratingsSummary}\n\nTeacher notes:\n${feedbackSummary || "None provided"}\n\nWrite:\n1. A warm, 2-sentence overall comment that acknowledges the student's effort and intellectual engagement\n2. Two specific strengths (1 sentence each, referencing what they did well)\n3. Two specific, actionable next steps (1 sentence each, framed as "Try..." or "Consider...")\n\nReturn ONLY valid JSON:\n{\n  "overallComment": "...",\n  "strengths": ["...", "..."],\n  "nextSteps": ["...", "..."]\n}`;

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-SandBox-Key": APP_KEY, "X-SandBox-Token": SANDBOX_TOKEN },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 500, mode: "assess", messages: [{ role: "user", content: prompt }] })
      });
      const data = await res.json();
      if (!res.ok) {
        setAssessError(data.error || "Something went wrong generating feedback. Please try again.");
        setAssessLoading(false);
        return;
      }
      const text = data.content?.find(b => b.type === "text")?.text || "";
      const clean = text.replace(/```json|```/g, "").trim();
      setAiSuggestions(JSON.parse(clean));
    } catch (e) {
      setAssessError("Something went wrong generating feedback. Please try again.");
    }
    setAssessLoading(false);
  };

  const getToday = () => new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const downloadAssessmentDoc = (type) => {
    const today = getToday();
    const name = studentName || "Student";
    const episode = episodeTitle || "Podcast Episode";

    if (type === "pdf") {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>The SandBox Student Assessment</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Poppins:wght@400;500&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Poppins', sans-serif; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; }
            .doc-header { border-bottom: 4px solid #2e7d32; padding-bottom: 20px; margin-bottom: 28px; }
            .doc-title { font-family: 'Montserrat', sans-serif; font-size: 1.6rem; font-weight: 800; color: #1a1a1a; }
            .doc-subtitle { font-family: 'Montserrat', sans-serif; font-size: 0.85rem; font-weight: 600; color: #2e7d32; text-transform: uppercase; letter-spacing: 1px; margin-top: 4px; }
            .doc-meta { margin-top: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
            .doc-meta-label { font-weight: 700; color: #2e7d32; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 1px; font-family: 'Montserrat', sans-serif; }
            .section { margin-bottom: 24px; }
            .section-title { font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px; color: #2e7d32; border-bottom: 1px solid #e0e0e0; padding-bottom: 6px; margin-bottom: 12px; }
            .rating-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0; font-size: 0.88rem; }
            .rating-label { font-weight: 600; }
            .rating-value { color: #2e7d32; font-weight: 700; }
            .rating-note { font-size: 0.82rem; color: #666; margin-top: 3px; padding-left: 8px; font-style: italic; }
            .feedback-box { background: #f8f8f8; border-left: 4px solid #2e7d32; padding: 14px 16px; border-radius: 0 6px 6px 0; margin-bottom: 10px; font-size: 0.9rem; line-height: 1.6; }
            .feedback-item { padding: 8px 0; border-bottom: 1px solid #f0f0f0; font-size: 0.88rem; line-height: 1.5; }
            .footnote { margin-top: 40px; padding-top: 12px; border-top: 1px solid #e0e0e0; font-size: 0.75rem; color: #999; font-style: italic; }
          </style>
        </head>
        <body>
          <div class="doc-header">
            <div class="doc-title">The SandBox Student Assessment</div>
            <div class="doc-subtitle">High School Edition</div>
            <div class="doc-meta">
              <div><div class="doc-meta-label">Student</div><div>${name}</div></div>
              <div><div class="doc-meta-label">Episode Title</div><div>${episode}</div></div>
              <div><div class="doc-meta-label">Date</div><div>${today}</div></div>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Verbal Skills Ratings</div>
            ${VERBAL_SKILLS.map(s => `
              <div class="rating-row">
                <div>
                  <div class="rating-label">${s.label}</div>
                  ${feedback[s.id] ? `<div class="rating-note">${feedback[s.id]}</div>` : ""}
                </div>
                <div class="rating-value">${RATINGS.find(r => r.value === ratings[s.id])?.label || "Not rated"}</div>
              </div>
            `).join("")}
          </div>
          <div class="section">
            <div class="section-title">Project Feedback</div>
            <div class="feedback-box">${aiSuggestions.overallComment}</div>
          </div>
          <div class="section">
            <div class="section-title">What's Working Well</div>
            ${aiSuggestions.strengths.map(s => `<div class="feedback-item">✓ ${s}</div>`).join("")}
          </div>
          <div class="section">
            <div class="section-title">Next Steps to Try</div>
            ${aiSuggestions.nextSteps.map(s => `<div class="feedback-item">→ ${s}</div>`).join("")}
          </div>
          <div class="footnote">Generated by The SandBox Student Podcast Assessment Tool — ${today}</div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 500);
    }

    if (type === "word") {
      const ratingsRows = VERBAL_SKILLS.map(s => `
        <tr>
          <td style="padding:8px;border:1px solid #ccc;font-weight:600;">${s.label}${feedback[s.id] ? `<br/><span style="font-weight:400;font-style:italic;font-size:11px;color:#666;">${feedback[s.id]}</span>` : ""}</td>
          <td style="padding:8px;border:1px solid #ccc;color:#2e7d32;font-weight:700;">${RATINGS.find(r => r.value === ratings[s.id])?.label || "Not rated"}</td>
        </tr>
      `).join("");

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8"><title>The SandBox Student Assessment</title>
        <style>
          body { font-family: Calibri, sans-serif; color: #1a1a1a; margin: 40px; }
          h1 { font-size: 22px; font-weight: 800; color: #1a1a1a; border-bottom: 3px solid #2e7d32; padding-bottom: 10px; }
          h2 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #2e7d32; margin: 20px 0 8px; }
          .meta-table { width: 100%; margin-bottom: 20px; }
          .meta-table td { padding: 4px 8px; font-size: 13px; }
          .meta-label { font-weight: 700; color: #2e7d32; font-size: 10px; text-transform: uppercase; }
          table.ratings { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          .feedback-box { background: #f8f8f8; border-left: 4px solid #2e7d32; padding: 12px 16px; margin-bottom: 10px; font-size: 13px; line-height: 1.6; }
          .feedback-item { padding: 6px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px; line-height: 1.5; }
          .footnote { margin-top: 40px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #999; font-style: italic; }
        </style>
        </head>
        <body>
          <h1>The SandBox Student Assessment — High School Edition</h1>
          <table class="meta-table">
            <tr><td><span class="meta-label">Student</span><br/>${name}</td><td><span class="meta-label">Episode Title</span><br/>${episode}</td><td><span class="meta-label">Date</span><br/>${today}</td></tr>
          </table>
          <h2>Verbal Skills Ratings</h2>
          <table class="ratings">
            <tr style="background:#f0f0f0;"><th style="padding:8px;border:1px solid #ccc;text-align:left;">Skill</th><th style="padding:8px;border:1px solid #ccc;text-align:left;">Rating</th></tr>
            ${ratingsRows}
          </table>
          <h2>Project Feedback</h2>
          <div class="feedback-box">${aiSuggestions.overallComment}</div>
          <h2>What's Working Well</h2>
          ${aiSuggestions.strengths.map(s => `<div class="feedback-item">✓ ${s}</div>`).join("")}
          <h2>Next Steps to Try</h2>
          ${aiSuggestions.nextSteps.map(s => `<div class="feedback-item">→ ${s}</div>`).join("")}
          <div class="footnote">Generated by The SandBox Student Podcast Assessment Tool — ${today}</div>
        </body>
        </html>
      `;

      const blob = new Blob(["\ufeff", html], { type: "application/msword" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SandBox-HS-Assessment-${name.replace(/\s+/g, "-")}-${today.replace(/\s+/g, "-")}.doc`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f4f4", fontFamily: "'Montserrat', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Poppins:ital,wght@0,400;0,500;0,600;1,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f4f4f4; }
        .hero { background: #1a1a1a; padding: 28px 32px 24px; text-align: center; position: relative; overflow: hidden; border-bottom: 4px solid #3a8f3a; }
        .hero::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: repeating-linear-gradient(-45deg, transparent, transparent 40px, rgba(46,125,50,0.04) 40px, rgba(46,125,50,0.04) 80px); pointer-events: none; }
        .hero-logo { max-width: 420px; width: 90%; height: auto; display: block; margin: 0 auto 6px; position: relative; z-index: 1; }
        .hero-sub { color: #7ec87e; font-family: 'Poppins', sans-serif; font-size: 0.85rem; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; position: relative; z-index: 1; }
        .hero-badge { display: inline-block; background: #2e7d32; color: white; font-family: 'Montserrat', sans-serif; font-size: 0.7rem; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; padding: 4px 14px; border-radius: 20px; margin-top: 8px; position: relative; z-index: 1; }
        .tabs { display: flex; background: #111; border-bottom: 3px solid #2e7d32; }
        .tab { flex: 1; padding: 15px; text-align: center; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 0.85rem; font-weight: 700; color: #666; transition: all 0.2s; letter-spacing: 1px; text-transform: uppercase; border: none; background: none; }
        .tab.active { background: #f4f4f4; color: #1a1a1a; border-top: 3px solid #2e7d32; margin-top: -3px; }
        .tab:hover:not(.active) { background: #1e1e1e; color: #7ec87e; }
        .container { max-width: 780px; margin: 0 auto; padding: 32px 24px; }
        .card { background: white; border-radius: 10px; padding: 28px; margin-bottom: 20px; box-shadow: 0 2px 16px rgba(0,0,0,0.08); border: 1px solid #e0e0e0; border-top: 4px solid #2e7d32; }
        .card-title { font-family: 'Montserrat', sans-serif; font-size: 1.2rem; font-weight: 800; color: #1a1a1a; margin-bottom: 4px; }
        .card-sub { color: #777; font-size: 0.87rem; margin-bottom: 22px; font-family: 'Poppins', sans-serif; font-style: italic; }
        .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
        .form-group { display: flex; flex-direction: column; gap: 6px; }
        .form-group label { font-size: 0.72rem; font-weight: 700; color: #2e7d32; letter-spacing: 1.2px; text-transform: uppercase; font-family: 'Montserrat', sans-serif; }
        select, input, textarea { padding: 10px 14px; border: 2px solid #e0e0e0; border-radius: 7px; font-family: 'Poppins', sans-serif; font-size: 0.92rem; color: #1a1a1a; background: #fafafa; transition: border-color 0.2s; outline: none; width: 100%; }
        select:focus, input:focus, textarea:focus { border-color: #2e7d32; box-shadow: 0 0 0 3px rgba(46,125,50,0.1); }
        textarea { resize: vertical; min-height: 80px; }
        .btn { padding: 13px 28px; border-radius: 7px; font-family: 'Montserrat', sans-serif; font-size: 0.88rem; font-weight: 700; cursor: pointer; border: none; transition: all 0.2s; letter-spacing: 0.8px; text-transform: uppercase; }
        .btn-primary { background: #2e7d32; color: white; }
        .btn-primary:hover { background: #1b5e20; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(46,125,50,0.35); }
        .btn-primary:disabled { background: #aaa; transform: none; box-shadow: none; cursor: not-allowed; }
        .btn-secondary { background: transparent; color: #2e7d32; border: 2px solid #2e7d32; }
        .btn-secondary:hover { background: #2e7d32; color: white; }
        .btn-download { background: #1a1a1a; color: #7ec87e; border: 2px solid #2e7d32; }
        .btn-download:hover { background: #2e7d32; color: white; }
        .idea-card { background: #fafafa; border: 2px solid #e8e8e8; border-left: 5px solid #2e7d32; border-radius: 8px; padding: 22px; margin-bottom: 14px; transition: box-shadow 0.2s; }
        .idea-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .idea-number { display: inline-block; background: #2e7d32; color: white; font-size: 0.68rem; font-weight: 800; padding: 3px 10px; border-radius: 20px; margin-bottom: 10px; font-family: 'Montserrat', sans-serif; letter-spacing: 1.5px; text-transform: uppercase; }
        .idea-title { font-family: 'Montserrat', sans-serif; font-size: 1.1rem; font-weight: 800; color: #1a1a1a; margin-bottom: 8px; }
        .idea-desc { color: #555; font-size: 0.91rem; line-height: 1.65; margin-bottom: 10px; font-family: 'Poppins', sans-serif; }
        .idea-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
        .tag { background: #e8f5e9; color: #2e7d32; font-size: 0.75rem; padding: 4px 12px; border-radius: 20px; font-family: 'Montserrat', sans-serif; font-weight: 700; border: 1px solid #c8e6c9; }
        .hook-box { background: #f1f8f1; border-left: 3px solid #4caf50; padding: 10px 14px; border-radius: 0 6px 6px 0; margin-top: 10px; }
        .hook-box p { color: #2e7d32; font-size: 0.88rem; font-style: italic; font-family: 'Poppins', sans-serif; }
        .error { background: #fdecea; border: 1px solid #f5c6c6; color: #b71c1c; padding: 12px 16px; border-radius: 7px; font-size: 0.88rem; margin-bottom: 16px; font-family: 'Poppins', sans-serif; }
        .loading-dots { display: flex; gap: 6px; justify-content: center; padding: 32px; }
        .dot { width: 10px; height: 10px; background: #2e7d32; border-radius: 50%; animation: bounce 1.2s infinite; }
        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes bounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-12px); } }
        .rating-grid { display: grid; gap: 14px; }
        .skill-row { background: #fafafa; border: 1.5px solid #e8e8e8; border-radius: 8px; padding: 18px; }
        .skill-label { font-family: 'Montserrat', sans-serif; font-size: 0.95rem; font-weight: 800; color: #1a1a1a; margin-bottom: 3px; }
        .skill-desc { color: #888; font-size: 0.82rem; font-style: italic; margin-bottom: 12px; font-family: 'Poppins', sans-serif; }
        .rating-options { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
        .rating-btn { padding: 7px 14px; border-radius: 20px; border: 2px solid #e0e0e0; background: white; cursor: pointer; font-size: 0.78rem; font-family: 'Montserrat', sans-serif; font-weight: 700; transition: all 0.15s; color: #444; text-transform: uppercase; letter-spacing: 0.5px; }
        .rating-btn.selected { border-color: #2e7d32; background: #2e7d32; color: white; }
        .rating-btn:hover:not(.selected) { border-color: #4caf50; background: #f1f8e9; color: #2e7d32; }
        .rating-btn-wrap { position: relative; }
        .rating-tooltip {
          position: absolute; bottom: calc(100% + 9px); left: 50%; transform: translateX(-50%) translateY(4px);
          width: 220px; background: #1a1a1a; color: #fff; font-family: 'Poppins', sans-serif; font-size: 0.72rem;
          font-weight: 400; font-style: normal; line-height: 1.45; text-transform: none; letter-spacing: normal;
          padding: 10px 12px; border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,0.28);
          opacity: 0; visibility: hidden; pointer-events: none; transition: opacity 0.15s ease, transform 0.15s ease;
          z-index: 30;
        }
        .rating-tooltip::after {
          content: ""; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          border: 6px solid transparent; border-top-color: #1a1a1a;
        }
        .rating-btn-wrap:hover .rating-tooltip { opacity: 1; visibility: visible; transform: translateX(-50%) translateY(0); }
        .feedback-input { width: 100%; padding: 8px 12px; border: 1.5px solid #e0e0e0; border-radius: 6px; font-family: 'Poppins', sans-serif; font-size: 0.87rem; color: #1a1a1a; background: #fafafa; resize: none; outline: none; }
        .feedback-input:focus { border-color: #2e7d32; }
        .divider { height: 1px; background: #e8e8e8; margin: 22px 0; }
        .section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
        .section-icon { font-size: 1.4rem; }
        .assessment-preview { background: white; border-radius: 10px; margin-top: 20px; box-shadow: 0 2px 16px rgba(0,0,0,0.08); border: 1px solid #e0e0e0; overflow: hidden; }
        .preview-header { background: #1a1a1a; padding: 24px 28px; border-bottom: 4px solid #2e7d32; }
        .preview-title { font-family: 'Montserrat', sans-serif; font-size: 1.3rem; font-weight: 800; color: white; margin-bottom: 4px; }
        .preview-title-sub { font-family: 'Montserrat', sans-serif; font-size: 0.72rem; font-weight: 700; color: #7ec87e; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 12px; }
        .preview-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .preview-meta-label { font-size: 0.65rem; font-weight: 700; color: #7ec87e; text-transform: uppercase; letter-spacing: 1px; font-family: 'Montserrat', sans-serif; margin-bottom: 2px; }
        .preview-meta-value { font-size: 0.9rem; color: #f0f0f0; font-family: 'Poppins', sans-serif; }
        .download-bar { display: flex; gap: 12px; padding: 16px 28px; background: #f8f8f8; border-bottom: 1px solid #e0e0e0; }
        .preview-body { padding: 28px; }
        .preview-section { margin-bottom: 24px; }
        .preview-section-title { font-family: 'Montserrat', sans-serif; font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 1.5px; color: #2e7d32; border-bottom: 2px solid #e8f5e9; padding-bottom: 6px; margin-bottom: 14px; }
        .preview-ratings-table { width: 100%; border-collapse: collapse; }
        .preview-ratings-table th { text-align: left; font-family: 'Montserrat', sans-serif; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #888; padding: 8px 12px; background: #f8f8f8; border: 1px solid #e8e8e8; }
        .preview-ratings-table td { padding: 10px 12px; border: 1px solid #e8e8e8; font-family: 'Poppins', sans-serif; font-size: 0.88rem; }
        .preview-ratings-table tr:nth-child(even) td { background: #fafafa; }
        .rating-chip { display: inline-block; background: #e8f5e9; color: #2e7d32; font-weight: 700; font-size: 0.78rem; padding: 3px 10px; border-radius: 20px; font-family: 'Montserrat', sans-serif; }
        .teacher-note { font-size: 0.8rem; color: #888; font-style: italic; margin-top: 4px; }
        .preview-feedback-box { background: #f8faf8; border-left: 4px solid #2e7d32; padding: 14px 16px; border-radius: 0 6px 6px 0; font-family: 'Poppins', sans-serif; font-size: 0.91rem; line-height: 1.65; color: #333; margin-bottom: 10px; }
        .preview-feedback-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 0; border-bottom: 1px solid #f0f0f0; font-family: 'Poppins', sans-serif; font-size: 0.9rem; line-height: 1.55; color: #333; }
        .preview-feedback-icon { color: #2e7d32; font-weight: 800; flex-shrink: 0; margin-top: 1px; }
        .preview-footnote { text-align: center; font-size: 0.75rem; color: #bbb; font-style: italic; font-family: 'Poppins', sans-serif; padding-top: 16px; border-top: 1px solid #f0f0f0; margin-top: 8px; }
        .preview-reset-bar { padding: 16px 28px; background: #f8f8f8; border-top: 1px solid #e0e0e0; }
        .footer { text-align: center; padding: 24px; font-size: 0.75rem; color: #999; font-family: 'Poppins', sans-serif; border-top: 1px solid #e0e0e0; margin-top: 20px; }
        @media (max-width: 600px) {
          .form-row { grid-template-columns: 1fr; }
          .hero-logo { max-width: 300px; }
          .rating-options { gap: 6px; }
          .rating-btn { padding: 6px 10px; font-size: 0.72rem; }
          .preview-meta { grid-template-columns: 1fr; }
          .download-bar { flex-direction: column; }
        }
      `}</style>

      <div className="hero">
        <img src="/DTM-TheSandBox-white.png" alt="DTM The SandBox" className="hero-logo" onError={(e) => { e.target.style.display = "none"; }} />
        <div className="hero-sub">The SandBox — Podcast Idea Generator & Assessment Tool</div>
        <div className="hero-badge">High School Edition — Grades 9-12</div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === "generate" ? "active" : ""}`} onClick={() => setActiveTab("generate")}>💡 Generate Ideas</button>
        {!IS_STUDENT && (
          <button className={`tab ${activeTab === "assess" ? "active" : ""}`} onClick={() => setActiveTab("assess")}>📋 Assess Students</button>
        )}
      </div>

      {activeTab === "generate" && (
        <div className="container">
          <div className="card">
            <div className="section-header">
              <span className="section-icon">🗂️</span>
              <div>
                <div className="card-title">Tell us about your class</div>
                <div className="card-sub">We'll generate college-prep podcast topics your high school students will love</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Subject *</label>
                <select value={subject} onChange={e => { setSubject(e.target.value); setStandard(""); }}>
                  <option value="">Select subject...</option>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Grade Level *</label>
                <select value={grade} onChange={e => setGrade(e.target.value)}>
                  <option value="">Select grade...</option>
                  {GRADE_LEVELS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Standards Focus</label>
                <select value={standard} onChange={e => setStandard(e.target.value)} disabled={!subject}>
                  <option value="">Select standard area...</option>
                  {availableStandards.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Preferred Format</label>
                <select value={podcastFormat} onChange={e => setPodcastFormat(e.target.value)}>
                  <option value="">Any format</option>
                  <option>Interview style</option>
                  <option>Debate / Two perspectives</option>
                  <option>Investigative report</option>
                  <option>Narrative storytelling</option>
                  <option>Explainer / How-to</option>
                  <option>Roundtable discussion</option>
                  <option>News-style report</option>
                  <option>Documentary style</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label>Current Unit or Topic (optional)</label>
              <input type="text" placeholder="e.g. The Civil Rights Movement, Macroeconomics, Shakespeare..." value={customTopic} onChange={e => setCustomTopic(e.target.value)} />
            </div>
            {error && <div className="error">⚠️ {error}</div>}
            <button className="btn btn-primary" onClick={generateIdeas} disabled={loading} style={{ width: "100%" }}>
              {loading ? "Generating ideas..." : "✨ Generate Podcast Ideas"}
            </button>
          </div>

          {loading && <div className="loading-dots"><div className="dot"></div><div className="dot"></div><div className="dot"></div></div>}

          {ideas && !loading && (
            <div>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem", fontWeight: 700, color: "#2e7d32", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "1px" }}>
                🎧 5 ideas tailored for your class:
              </div>
              {ideas.ideas.map((idea, i) => (
                <div className="idea-card" key={i}>
                  <div className="idea-number">🎙️ Idea</div>
                  <div className="idea-title">{idea.title}</div>
                  <div className="idea-desc">{idea.description}</div>
                  <div className="idea-meta">
                    <span className="tag">🎯 {idea.objective}</span>
                    <span className="tag">🎙️ {idea.format}</span>
                  </div>
                  <div className="hook-box">
                    <p>💬 Hook: <strong>{idea.hook}</strong></p>
                  </div>
                </div>
              ))}
              <button className="btn btn-secondary" onClick={generateIdeas} style={{ width: "100%", marginTop: "8px" }}>
                🔄 Generate 5 More Ideas
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === "assess" && !IS_STUDENT && (
        <div className="container">
          <div className="card">
            <div className="section-header">
              <span className="section-icon">🎤</span>
              <div>
                <div className="card-title">Podcast Verbal Skills Assessment</div>
                <div className="card-sub">Rate each area to generate personalized, growth-focused feedback</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>First Name Only</label>
                <input type="text" placeholder="e.g. Liam, Sarah, Marcus" value={studentName} onChange={e => setStudentName(e.target.value.replace(/\s/g, ""))} maxLength={12} />
                <div style={{fontSize:"0.75rem", color:"#888", fontStyle:"italic", marginTop:"4px"}}>To protect student privacy, please use first name only</div>
              </div>
              <div className="form-group">
                <label>Episode Title</label>
                <input type="text" placeholder="Title of their podcast episode..." value={episodeTitle} onChange={e => setEpisodeTitle(e.target.value)} />
              </div>
            </div>
            <div className="divider" />
            <div className="rating-grid">
              {VERBAL_SKILLS.map(skill => (
                <div className="skill-row" key={skill.id}>
                  <div className="skill-label">{skill.label}</div>
                  <div className="skill-desc">{skill.desc}</div>
                  <div className="rating-options">
                    {RATINGS.map(r => (
                      <div key={r.value} className="rating-btn-wrap">
                        <button className={`rating-btn ${ratings[skill.id] === r.value ? "selected" : ""}`} onClick={() => setRatings(prev => ({ ...prev, [skill.id]: r.value }))}>
                          {r.label}
                        </button>
                        <div className="rating-tooltip" role="tooltip">{RUBRIC_LEVEL_DESCRIPTIONS[r.label]}</div>
                      </div>
                    ))}
                  </div>
                  <textarea className="feedback-input" rows={2} placeholder={`Optional: add a specific observation about ${skill.label.toLowerCase()}...`} value={feedback[skill.id] || ""} onChange={e => setFeedback(prev => ({ ...prev, [skill.id]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="divider" />
            {assessError && <div className="error">⚠️ {assessError}</div>}
            <button className="btn btn-primary" onClick={generateFeedback} disabled={Object.keys(ratings).length < VERBAL_SKILLS.length || assessLoading} style={{ width: "100%" }}>
              {assessLoading ? "Generating feedback..." : Object.keys(ratings).length < VERBAL_SKILLS.length ? `Rate all ${VERBAL_SKILLS.length} areas to generate feedback` : "✨ Generate Student Feedback"}
            </button>
          </div>

          {assessLoading && <div className="loading-dots"><div className="dot"></div><div className="dot"></div><div className="dot"></div></div>}

          {aiSuggestions && !assessLoading && (
            <div className="assessment-preview">
              <div className="download-bar">
                <button className="btn btn-download" onClick={() => downloadAssessmentDoc("pdf")} style={{ flex: 1 }}>⬇ Download as PDF</button>
                <button className="btn btn-download" onClick={() => downloadAssessmentDoc("word")} style={{ flex: 1 }}>⬇ Download as Word Doc</button>
              </div>
              <div className="preview-header">
                <div className="preview-title">The SandBox Student Assessment</div>
                <div className="preview-title-sub">High School Edition</div>
                <div className="preview-meta">
                  <div><div className="preview-meta-label">Student</div><div className="preview-meta-value">{studentName || "Student"}</div></div>
                  <div><div className="preview-meta-label">Episode Title</div><div className="preview-meta-value">{episodeTitle || "Podcast Episode"}</div></div>
                  <div><div className="preview-meta-label">Date</div><div className="preview-meta-value">{getToday()}</div></div>
                </div>
              </div>
              <div className="preview-body">
                <div className="preview-section">
                  <div className="preview-section-title">Verbal Skills Ratings</div>
                  <table className="preview-ratings-table">
                    <thead><tr><th>Skill</th><th>Rating</th></tr></thead>
                    <tbody>
                      {VERBAL_SKILLS.map(skill => (
                        <tr key={skill.id}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{skill.label}</div>
                            {feedback[skill.id] && <div className="teacher-note">{feedback[skill.id]}</div>}
                          </td>
                          <td><span className="rating-chip">{RATINGS.find(r => r.value === ratings[skill.id])?.label || "Not rated"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="preview-section">
                  <div className="preview-section-title">Project Feedback</div>
                  <div className="preview-feedback-box">{aiSuggestions.overallComment}</div>
                </div>
                <div className="preview-section">
                  <div className="preview-section-title">⭐ What's Working Well</div>
                  {aiSuggestions.strengths.map((s, i) => (
                    <div className="preview-feedback-item" key={i}><span className="preview-feedback-icon">✓</span><span>{s}</span></div>
                  ))}
                </div>
                <div className="preview-section">
                  <div className="preview-section-title">🚀 Next Steps to Try</div>
                  {aiSuggestions.nextSteps.map((s, i) => (
                    <div className="preview-feedback-item" key={i}><span className="preview-feedback-icon">→</span><span>{s}</span></div>
                  ))}
                </div>
                <div className="preview-footnote">Generated by The SandBox Student Podcast Assessment Tool — {getToday()}</div>
              </div>
              <div className="preview-reset-bar">
                <button className="btn btn-secondary" style={{ width: "100%" }} onClick={() => { setRatings({}); setFeedback({}); setStudentName(""); setEpisodeTitle(""); setAiSuggestions(null); }}>
                  🔄 Start New Assessment
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="footer">
        The SandBox and all of its associated tools are Copyright DumpTruck Media LLC 2026
      </div>
    </div>
  );
}
